import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, authFetch } from '../utils/auth';
import '../styles/auth.css';

export default function AdminPortal() {
  const { user, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteResult, setInviteResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [selectedInvites, setSelectedInvites] = useState(new Set());
  const [copiedInviteId, setCopiedInviteId] = useState(null);

  useEffect(() => {
    if (!isAdmin) { navigate('/login'); return; }
    loadUsers();
    loadInvites();
  }, [isAdmin]);

  const loadUsers = async () => {
    try {
      const res = await authFetch('/api/admin/users');
      if (res.ok) setUsers(await res.json());
    } catch {}
  };

  const loadInvites = async () => {
    try {
      const res = await authFetch('/api/admin/invites');
      if (res.ok) setInvites(await res.json());
    } catch {}
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    setError('');
    setInviteResult(null);
    setCopied(false);
    setLoading(true);
    try {
      const res = await authFetch('/api/admin/invite', {
        method: 'POST',
        body: JSON.stringify({ email: inviteEmail })
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error); setLoading(false); return; }
      setInviteResult(data);
      setInviteEmail('');
      setLoading(false);
      loadUsers();
      loadInvites();
    } catch { setError('Connection error'); setLoading(false); }
  };

  const resendInvite = async (id) => {
    try {
      const res = await authFetch(`/api/admin/invites/${id}/resend`, { method: 'POST' });
      if (res.ok) loadInvites();
    } catch {}
  };

  const deleteInvites = async (ids) => {
    if (!confirm(`Delete ${ids.length} invite(s)? This cannot be undone.`)) return;
    try {
      await authFetch('/api/admin/invites/delete', {
        method: 'POST',
        body: JSON.stringify({ ids })
      });
      setSelectedInvites(new Set());
      loadInvites();
    } catch {}
  };

  const toggleInviteSelect = (id) => {
    setSelectedInvites(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAllInvites = () => {
    if (selectedInvites.size === invites.length) setSelectedInvites(new Set());
    else setSelectedInvites(new Set(invites.map(i => i.id)));
  };

  const copyInviteLink = (url, id) => {
    navigator.clipboard.writeText(url);
    setCopiedInviteId(id);
    setTimeout(() => setCopiedInviteId(null), 2000);
  };

  const toggleActive = async (id) => {
    await authFetch(`/api/admin/users/${id}/toggle-active`, { method: 'POST' });
    loadUsers();
  };

  const deleteUser = async (id, email) => {
    if (!confirm(`Delete user ${email}? This cannot be undone.`)) return;
    await authFetch(`/api/admin/users/${id}`, { method: 'DELETE' });
    loadUsers();
  };

  const copyLink = () => {
    if (inviteResult?.invite_url) {
      navigator.clipboard.writeText(inviteResult.invite_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const statusColor = (s) => s === 'pending' ? '#00ff9d' : s === 'expired' ? '#ff4d4d' : '#64748b';
  const statusLabel = (s) => s === 'pending' ? 'Pending' : s === 'expired' ? 'Expired' : 'Used';

  const timeAgo = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    const now = new Date();
    const diff = now - d;
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  if (!isAdmin) return null;

  return (
    <div className="auth-page" style={{ alignItems: 'flex-start', paddingTop: 40 }}>
      <div className="auth-card" style={{ maxWidth: 750 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div>
            <h1 style={{ color: '#00ff9d', fontSize: 22, margin: '0 0 4px', fontWeight: 700 }}>Admin Portal</h1>
            <p style={{ color: '#64748b', fontSize: 13, margin: 0 }}>{user?.email}</p>
          </div>
          <button className="auth-btn secondary" style={{ width: 'auto', padding: '8px 18px', fontSize: 13 }} onClick={handleLogout}>Logout</button>
        </div>

        <div style={{ marginBottom: 28 }}>
          <h2 style={{ color: '#e2e8f0', fontSize: 16, margin: '0 0 12px' }}>Invite New User</h2>
          {error && <div className="auth-error">{error}</div>}
          <form onSubmit={handleInvite} style={{ display: 'flex', gap: 10 }}>
            <div className="auth-field" style={{ flex: 1, margin: 0 }}>
              <input type="email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="user@example.com" required />
            </div>
            <button className="auth-btn" style={{ width: 'auto', padding: '12px 24px', margin: 0 }} disabled={loading}>{loading ? 'Sending...' : 'Send Invite'}</button>
          </form>
          {inviteResult && (
            <div style={{ marginTop: 12 }}>
              <div className="auth-success">
                {inviteResult.email_sent ? 'Invite email sent! Link expires in 24 hours.' : 'Invite created (email could not be sent). Link expires in 24 hours.'}
              </div>
              <div className="invite-url-box">
                <input readOnly value={inviteResult.invite_url} />
                <button onClick={copyLink}>{copied ? 'Copied!' : 'Copy Link'}</button>
              </div>
              <p style={{ color: '#64748b', fontSize: 12, margin: '4px 0 0' }}>Share this link via WhatsApp, SMS, or any messaging app.</p>
            </div>
          )}
        </div>

        <div style={{ marginBottom: 28 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <h2 style={{ color: '#e2e8f0', fontSize: 16, margin: 0 }}>Sent Invites ({invites.length})</h2>
            {selectedInvites.size > 0 && (
              <button className="auth-btn danger" style={{ width: 'auto', padding: '6px 16px', fontSize: 12, margin: 0 }} onClick={() => deleteInvites([...selectedInvites])}>
                Delete Selected ({selectedInvites.size})
              </button>
            )}
          </div>
          {invites.length === 0 ? (
            <p style={{ color: '#64748b', fontSize: 13 }}>No invites sent yet.</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="admin-users-table">
                <thead>
                  <tr>
                    <th style={{ width: 32 }}>
                      <input type="checkbox" checked={selectedInvites.size === invites.length && invites.length > 0} onChange={toggleAllInvites} style={{ accentColor: '#00ff9d' }} />
                    </th>
                    <th>Email</th>
                    <th>Status</th>
                    <th>Sent</th>
                    <th>Expires</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invites.map(inv => (
                    <tr key={inv.id}>
                      <td>
                        <input type="checkbox" checked={selectedInvites.has(inv.id)} onChange={() => toggleInviteSelect(inv.id)} style={{ accentColor: '#00ff9d' }} />
                      </td>
                      <td>{inv.email}</td>
                      <td><span style={{ color: statusColor(inv.status), fontSize: 12, fontWeight: 600 }}>{statusLabel(inv.status)}</span></td>
                      <td style={{ color: '#94a3b8', fontSize: 12 }}>{timeAgo(inv.created_at)}</td>
                      <td style={{ color: '#94a3b8', fontSize: 12 }}>{inv.status === 'pending' ? timeAgo(inv.expires_at).replace('ago', 'left').replace(/^0m left$/, 'soon') : '—'}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {inv.status !== 'used' && (
                          <button className="action-btn" onClick={() => resendInvite(inv.id)}>Resend</button>
                        )}
                        <button className="action-btn" onClick={() => copyInviteLink(inv.invite_url, inv.id)}>{copiedInviteId === inv.id ? 'Copied!' : 'Copy'}</button>
                        <button className="action-btn delete" onClick={() => deleteInvites([inv.id])}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div>
          <h2 style={{ color: '#e2e8f0', fontSize: 16, margin: '0 0 8px' }}>Users ({users.length})</h2>
          {users.length === 0 ? (
            <p style={{ color: '#64748b', fontSize: 13 }}>No users yet. Invite someone to get started.</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="admin-users-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Name</th>
                    <th>Status</th>
                    <th>2FA</th>
                    <th>Joined</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id}>
                      <td>{u.email}</td>
                      <td>{u.display_name || '—'}</td>
                      <td><span className={u.is_active ? 'status-active' : 'status-inactive'}>{u.is_active ? 'Active' : 'Inactive'}</span></td>
                      <td style={{ color: u.require_2fa ? '#00ff9d' : '#64748b' }}>{u.require_2fa ? 'On' : 'Off'}</td>
                      <td style={{ color: '#94a3b8', fontSize: 12 }}>{u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}</td>
                      <td>
                        <button className="action-btn" onClick={() => toggleActive(u.id)}>{u.is_active ? 'Disable' : 'Enable'}</button>
                        <button className="action-btn delete" onClick={() => deleteUser(u.id, u.email)}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
